import React from "react";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Save, Play, RotateCcw, Target, Share2, FileDown } from "lucide-react";
import AdvancedSettings from "@/components/AdvancedSettings";

const NumberField = ({ id, label, value, onChange, prefix, suffix, step = 1, min = 0, max }) => (
  <div>
    <Label htmlFor={id} className="text-[11px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold">
      {label}
    </Label>
    <div className="mt-2 flex items-center bg-white border border-[#E5E2DC] rounded-lg focus-within:border-[#2B5339] transition-colors">
      {prefix && <span className="pl-3 text-[#5C6B62] text-sm">{prefix}</span>}
      <Input
        id={id}
        data-testid={`input-${id}`}
        type="number"
        value={value}
        step={step}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        className="border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 text-[#1C2B23] font-mono-num"
      />
      {suffix && <span className="pr-3 text-[#5C6B62] text-sm">{suffix}</span>}
    </div>
  </div>
);

const AllocSlider = ({ label, value, onChange, color }) => (
  <div>
    <div className="flex items-baseline justify-between">
      <Label className="text-[11px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold flex items-center gap-2">
        <span className="inline-block w-2 h-2 rounded-full" style={{ background: color }} /> {label}
      </Label>
      <span className="font-mono-num text-sm text-[#1C2B23]">{value}%</span>
    </div>
    <Slider
      data-testid={`slider-${label.toLowerCase()}`}
      value={[value]}
      onValueChange={(v) => onChange(v[0])}
      max={100}
      step={1}
      className="mt-3"
    />
  </div>
);

export default function CalculatorForm({ inputs, setInputs, onRun, onSave, onReset, onGoalSeek, onShare, onExportPDF, loading }) {
  const updateAlloc = (key, val) => {
    // Re-normalize the other two proportionally to keep sum = 100
    const others = ["stocks", "bonds", "cash"].filter((k) => k !== key);
    const remaining = 100 - val;
    const sumOthers = inputs.allocation[others[0]] + inputs.allocation[others[1]];
    let a, b;
    if (sumOthers <= 0) {
      a = remaining / 2;
      b = remaining / 2;
    } else {
      a = Math.round((inputs.allocation[others[0]] / sumOthers) * remaining);
      b = remaining - a;
    }
    setInputs({
      ...inputs,
      allocation: { ...inputs.allocation, [key]: val, [others[0]]: a, [others[1]]: b },
    });
  };

  const sum = inputs.allocation.stocks + inputs.allocation.bonds + inputs.allocation.cash;

  return (
    <div
      data-testid="calculator-form"
      className="bg-white border border-[#E5E2DC] rounded-2xl p-6 sm:p-8 shadow-sm sticky top-24"
    >
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-display text-xl text-[#1C2B23]">Your plan</h3>
        <button
          data-testid="btn-reset"
          onClick={onReset}
          className="text-xs text-[#5C6B62] hover:text-[#2B5339] inline-flex items-center gap-1"
          type="button"
        >
          <RotateCcw size={12} /> reset
        </button>
      </div>

      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <NumberField
            id="current-age"
            label="Current age"
            value={inputs.current_age}
            onChange={(v) => setInputs({ ...inputs, current_age: v })}
            min={18}
            max={99}
            suffix="yrs"
          />
          <NumberField
            id="retirement-age"
            label="Retire at"
            value={inputs.retirement_age}
            onChange={(v) => setInputs({ ...inputs, retirement_age: v })}
            min={30}
            max={99}
            suffix="yrs"
          />
        </div>
        <NumberField
          id="life-expectancy"
          label="Life expectancy"
          value={inputs.life_expectancy}
          onChange={(v) => setInputs({ ...inputs, life_expectancy: v })}
          min={60}
          max={120}
          suffix="yrs"
        />

        <div className="h-px bg-[#E5E2DC] my-2" />

        <NumberField
          id="current-savings"
          label="Current savings"
          value={inputs.current_savings}
          onChange={(v) => setInputs({ ...inputs, current_savings: v })}
          prefix="$"
          step={1000}
        />
        <NumberField
          id="monthly-contribution"
          label="Monthly contribution"
          value={inputs.monthly_contribution}
          onChange={(v) => setInputs({ ...inputs, monthly_contribution: v })}
          prefix="$"
          step={50}
        />
        <NumberField
          id="annual-withdrawal"
          label="Annual withdrawal in retirement (today's $)"
          value={inputs.annual_withdrawal}
          onChange={(v) => setInputs({ ...inputs, annual_withdrawal: v })}
          prefix="$"
          step={1000}
        />
        <NumberField
          id="inflation-rate"
          label="Inflation rate"
          value={inputs.inflation_rate}
          onChange={(v) => setInputs({ ...inputs, inflation_rate: v })}
          suffix="%"
          step={0.1}
          min={0}
          max={20}
        />

        <div className="h-px bg-[#E5E2DC] my-2" />

        <div>
          <Label className="text-[11px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold">
            Asset allocation
          </Label>
          <div className="mt-4 space-y-5">
            <AllocSlider
              label="Stocks"
              color="#2B5339"
              value={inputs.allocation.stocks}
              onChange={(v) => updateAlloc("stocks", v)}
            />
            <AllocSlider
              label="Bonds"
              color="#728A7C"
              value={inputs.allocation.bonds}
              onChange={(v) => updateAlloc("bonds", v)}
            />
            <AllocSlider
              label="Cash"
              color="#D4B886"
              value={inputs.allocation.cash}
              onChange={(v) => updateAlloc("cash", v)}
            />
          </div>
          {sum !== 100 && (
            <p data-testid="alloc-warning" className="text-xs text-[#C26747] mt-3">
              Allocation totals {sum}% — adjust to equal 100%.
            </p>
          )}
        </div>

        <AdvancedSettings inputs={inputs} setInputs={setInputs} />
      </div>

      <div className="mt-7 flex gap-3">
        <button
          data-testid="btn-run-projection"
          onClick={onRun}
          disabled={loading}
          type="button"
          className="flex-1 bg-[#2B5339] text-white hover:bg-[#1E3B28] disabled:opacity-60 rounded-full px-5 py-3 transition-colors text-sm font-medium inline-flex items-center justify-center gap-2"
        >
          <Play size={14} /> {loading ? "Running…" : "Run projection"}
        </button>
        <button
          data-testid="btn-save-scenario"
          onClick={onSave}
          type="button"
          className="bg-transparent border border-[#2B5339] text-[#2B5339] hover:bg-[#F0EFEA] rounded-full px-4 py-3 transition-colors text-sm inline-flex items-center gap-2"
          title="Save to this browser"
        >
          <Save size={14} /> Save
        </button>
      </div>

      <button
        data-testid="btn-goal-seek"
        onClick={onGoalSeek}
        type="button"
        className="mt-3 w-full bg-[#F0EFEA] text-[#2B5339] hover:bg-[#E5E2DC] rounded-full py-3 transition-colors text-sm inline-flex items-center justify-center gap-2"
      >
        <Target size={14} /> Goal seek — find what it takes
      </button>

      <div className="mt-3 grid grid-cols-2 gap-3">
        <button
          data-testid="btn-share"
          type="button"
          onClick={onShare}
          className="bg-transparent border border-[#E5E2DC] text-[#1C2B23] hover:border-[#2B5339] hover:text-[#2B5339] rounded-full py-2.5 text-xs inline-flex items-center justify-center gap-2 transition-colors"
        >
          <Share2 size={13} /> Share link
        </button>
        <button
          data-testid="btn-export-pdf"
          type="button"
          onClick={onExportPDF}
          className="bg-transparent border border-[#E5E2DC] text-[#1C2B23] hover:border-[#2B5339] hover:text-[#2B5339] rounded-full py-2.5 text-xs inline-flex items-center justify-center gap-2 transition-colors"
        >
          <FileDown size={13} /> Export PDF
        </button>
      </div>
    </div>
  );
}
